import { useEffect } from "react";
import {
  insertVertexAt,
  moveVertex,
  removeVertexAt,
  type VertexTarget,
} from "../model/inp/editNetwork.ts";
import {
  canRemoveVertex,
  keyboardInsertIndex,
  keyboardInsertPoint,
  vertexPointAt,
  vertexPointCount,
  vertexTargetFor,
} from "../model/inp/vertexHandles.ts";
import { computeBounds } from "../model/inp/bounds.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { ClipboardData } from "../model/inp/clipboard.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { StoredProject } from "../model/storage.ts";
import type { EditMode } from "./editModeTools.ts";
import type { PendingDelete } from "./useDeleteFlow.ts";

interface UseKeyboardShortcutsOptions {
  selected: ElementRef | null;
  multiSelected: ElementRef[];
  clipboard: ClipboardData | null;
  pendingDelete: PendingDelete | null;
  dialogsOpen: Record<string, boolean>;
  project: SwmmProject | null;
  loaded: StoredProject | null;
  undoStack: unknown[];
  redoStack: unknown[];
  editGeometry: boolean;
  mode: EditMode;
  activeFocusedVertex: { target: VertexTarget; index: number } | null;
  setFocusedVertex: (value: { target: VertexTarget; index: number } | null) => void;
  mutateRawUndoable: (
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) => void;
  changeMode: (next: EditMode) => void;
  undo: () => void;
  redo: () => void;
  handleDelete: () => void;
  handleCopy: () => void;
  handlePaste: () => void;
  setSelected: (ref: ElementRef | null) => void;
  setMultiSelected: (refs: ElementRef[]) => void;
}

/**
 * Delete/Supr deletes the current selection, Ctrl+Z/Ctrl+Y (and Ctrl+
 * Shift+Z) drive undo/redo, Ctrl+C/Ctrl+V copy/paste the current selection,
 * plus keyboard-only vertex editing ([/]/Insert/+/arrows) while "Edit
 * geometry" is on. All ignored while typing in a field — for Delete/Ctrl+V
 * so this can't eat a real Delete/paste keystroke meant for a text field;
 * for undo/redo/copy so the browser's own text-field undo/copy runs instead
 * of reverting a network edit or overwriting the clipboard with plain text.
 * Delete is also ignored while a delete dialog is already up, so it can't
 * double-fire.
 */
export function useKeyboardShortcuts(opts: UseKeyboardShortcutsOptions) {
  const {
    selected,
    multiSelected,
    clipboard,
    pendingDelete,
    dialogsOpen,
    project,
    loaded,
    undoStack,
    redoStack,
    editGeometry,
    mode,
    activeFocusedVertex,
    setFocusedVertex,
    mutateRawUndoable,
    changeMode,
    undo,
    redo,
    handleDelete,
    handleCopy,
    handlePaste,
    setSelected,
    setMultiSelected,
  } = opts;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
      if (typing) return;
      // These global shortcuts used to stay live behind an open modal dialog
      // too — e.g. Ctrl+Z while "Options" was open would silently undo a
      // network edit in the background, since focus landing on a non-input
      // element inside the dialog never tripped the `typing` guard above.
      // `pendingDelete` covers AlertDialog/ConfirmDialog (never routed
      // through `useDialogToggles`); `dialogsOpen` covers every other one.
      if (pendingDelete || Object.values(dialogsOpen).some(Boolean)) return;
      const modifier = e.ctrlKey || e.metaKey;
      // Keyboard-only vertex editing — checked BEFORE the whole-element
      // Delete handler below so a focused vertex intercepts Delete instead
      // of deleting the whole element. Only while "Edit geometry" is on and
      // a link/subcatchment is selected.
      if (
        !modifier &&
        project &&
        editGeometry &&
        mode === "select" &&
        selected &&
        (selected.category === "link" || selected.category === "subcatchment")
      ) {
        const vertexTarget = vertexTargetFor(selected);
        if (vertexTarget) {
          const count = vertexPointCount(project, selected);
          const current = activeFocusedVertex;

          if (e.key === "[" || e.key === "]") {
            if (count > 0) {
              e.preventDefault();
              const dir = e.key === "]" ? 1 : -1;
              const nextIndex =
                current === null
                  ? dir === 1
                    ? 0
                    : count - 1
                  : (current.index + dir + count) % count;
              setFocusedVertex({ target: vertexTarget, index: nextIndex });
            }
            return;
          }
          if (e.key === "Insert" || e.key === "+") {
            e.preventDefault();
            const afterIndex = current?.index ?? null;
            const point = keyboardInsertPoint(project, selected, afterIndex);
            if (point) {
              const newIndex = keyboardInsertIndex(afterIndex);
              mutateRawUndoable((raw) => insertVertexAt(raw, vertexTarget, newIndex, point));
              setFocusedVertex({ target: vertexTarget, index: newIndex });
            }
            return;
          }
          if (current) {
            if (e.key === "Delete" || e.key === "Backspace") {
              e.preventDefault();
              if (canRemoveVertex(vertexTarget, project)) {
                mutateRawUndoable((raw) => removeVertexAt(raw, vertexTarget, current.index));
                const newCount = count - 1;
                setFocusedVertex(
                  newCount > 0
                    ? { target: vertexTarget, index: Math.min(current.index, newCount - 1) }
                    : null
                );
              }
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setFocusedVertex(null);
              return;
            }
            if (
              e.key === "ArrowUp" ||
              e.key === "ArrowDown" ||
              e.key === "ArrowLeft" ||
              e.key === "ArrowRight"
            ) {
              e.preventDefault();
              const point = vertexPointAt(project, selected, current.index);
              if (point) {
                const bounds = computeBounds(project);
                const span = bounds
                  ? Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 1)
                  : 100;
                const step = span * (e.shiftKey ? 0.02 : 0.005);
                const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
                const dy = e.key === "ArrowUp" ? step : e.key === "ArrowDown" ? -step : 0;
                mutateRawUndoable((raw) =>
                  moveVertex(raw, vertexTarget, current.index, {
                    x: point.x + dx,
                    y: point.y + dy,
                  })
                );
              }
              return;
            }
          }
        }
      }
      // Plain Escape clears whatever's selected. `.blur()` releases the
      // element's native DOM focus (still active from the click that
      // selected it) — found live: without this, a subsequent keypress
      // reclassified the lingering focus outline from plain `:focus` to
      // this app's own blue `:focus-visible` one instead of disappearing.
      if (e.key === "Escape" && mode === "select" && (selected || multiSelected.length > 0)) {
        e.preventDefault();
        setSelected(null);
        setMultiSelected([]);
        setFocusedVertex(null);
        (document.activeElement as HTMLElement | null)?.blur();
        return;
      }
      // Every add-* tool (and select-rect/select-lasso) needs a keyboard way
      // out — a rectangle/lasso drag has no visible "Cancel" affordance the
      // way add-subcatchment's own hint banner does.
      if (e.key === "Escape" && mode !== "select") {
        e.preventDefault();
        changeMode("select");
        return;
      }
      if (e.key === "Delete") {
        if (!selected || pendingDelete) return;
        e.preventDefault();
        handleDelete();
        return;
      }
      if (modifier && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
        return;
      }
      if (
        modifier &&
        (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))
      ) {
        e.preventDefault();
        redo();
        return;
      }
      if (modifier && e.key.toLowerCase() === "c") {
        e.preventDefault();
        handleCopy();
        return;
      }
      if (modifier && e.key.toLowerCase() === "v") {
        e.preventDefault();
        handlePaste();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // handleCopy/handlePaste/mutateRawUndoable/undo/redo/handleDelete/
    // changeMode/setSelected/setMultiSelected/setFocusedVertex are plain
    // (non-useCallback) functions rebuilt every App.tsx render, so the
    // closure this effect captures on mount would otherwise go stale the
    // moment any of them started reading different data — undo()/redo() in
    // particular close over `loaded`/the undo-redo stacks internally rather
    // than taking them as arguments. Cheap to just re-subscribe the whole
    // listener on every relevant change instead of memoizing 10 callbacks
    // through useCallback: clipboard/loaded/undoStack/redoStack below are
    // listed for exactly that reason (to force a re-subscribe that
    // recaptures handleCopy/handlePaste/undo/redo/mutateRawUndoable fresh)
    // even though `handleKeyDown` never reads them directly — removing them
    // would silently paste/copy/undo/redo against a stale snapshot instead
    // of eslint catching it, since none of the 10 callbacks above are
    // memoized enough for exhaustive-deps to know they depend on this state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selected,
    multiSelected,
    clipboard,
    pendingDelete,
    dialogsOpen,
    project,
    loaded,
    undoStack,
    redoStack,
    editGeometry,
    mode,
    activeFocusedVertex,
  ]);
}
